import styled, { Color, Spacing, BorderRadius, Font, css } from '@nfinite/react-hubstairs/styled'
import { ToggleStandalone } from 'components/Commons/Inputs/Toggle'
import { createGlobalStyle } from 'styled-components'

import IconComponent from 'components/Commons/Icon'
import CheckboxComponent from 'components/Commons/Inputs/Checkbox'
import SearchComponent from 'components/Commons/Search'

const INPUT_HEIGHT = 50

// The style needs to be global in order to still be applied when the select menu is wrapped in a React portal
export const ReactSelectGlobalStyle = createGlobalStyle`
  body {

    .nfinite-select__control, .nfinite-select-light__control {
      box-shadow: none;
      cursor: pointer;
    }

    .nfinite-select__control {
      background-color: ${Color.get('gray', 'light')};
      border: 1px solid ${Color.get('gray', 'light')};
      ${Spacing.generatePadding({ horizontal: 'xs' })}
      ${BorderRadius.generate({ all: 'm' })}
      // allows the input to expand on multi selection
      min-height: ${INPUT_HEIGHT}px;
      height: auto;
      &:hover {
        border-color: ${Color.get('gray', 'dark')};
      }
    }

    .nfinite-select-light__control {
      background-color: ${Color.get('transparent')};
      border: none;
      min-height: 0px;
    }

    .nfinite-select__placeholder {
      ${Spacing.generateMargin({ all: '0' })}
      color: ${Color.get('gray', 'dark')};
    }

    .nfinite-select__value-container {
      ${Spacing.generatePadding({ vertical: 'xs', left: 'xs', right: '0' })}
      ${Spacing.generateMargin({ all: '0' })}
    }
    .nfinite-select-light__value-container {
      ${Spacing.generatePadding({ all: '0' })}
      ${Spacing.generateMargin({ all: '0' })}
    }

    .nfinite-select__single-value, .nfinite-select__multi-value , .nfinite-select-light__single-value, .nfinite-select-light__multi-value {
      ${Spacing.generateMargin({ all: '0' })}
      background: transparent;
    }
    
    .nfinite-select__multi-value__label {
      padding: 2px 5px 2px 0
    }
    
    .nfinite-select-light__single-value, .nfinite-select-light__multi-value {
      ${Font.generate({ weight: 'bold' })}
    }
    
    .nfinite-select__single-value--is-disabled,.nfinite-select-light__single-value--is-disabled {
      color: ${Color.get('text', 'gray')};

      svg {
        opacity: 0.5;
      }
    }

    .nfinite-select__indicator-separator,.nfinite-select-light__indicator-separator {
      display: none;
    }

    .nfinite-select__dropdown-indicator{
      ${Spacing.generatePadding({ all: 'xs' })}
      ${Spacing.generateMargin({ all: '0' })}
    }

    .nfinite-select-light__dropdown-indicator{
      ${Spacing.generatePadding({ right: '0' })}
      ${Spacing.generateMargin({ all: '0' })}
    }

    .nfinite-select__menu-portal, .nfinite-select-light__menu-portal {
      z-index: 9992;

    }

    .nfinite-select__menu, .nfinite-select-light__menu {
      z-index: 9992;
      width: auto;
      min-width: 100%;
      margin-top: 4px;
      margin-bottom: 4px;
      ${BorderRadius.generate({ all: 'm' })}

    }

    .nfinite-select__menu-list ,.nfinite-select-light__menu-list {
      ${Spacing.generatePadding({ all: '0' })}
      ${BorderRadius.generate({ all: 'm' })}
      ::-webkit-scrollbar {
        width: 4px;
      }
    }

    .nfinite-select__option, .nfinite-select-light__option {
      cursor: pointer;
      color: ${Color.get('text')};
      ${Spacing.generatePadding({ vertical: 'xs', horizontal: 'm' })}
    }
    
    .nfinite-select__option--is-disabled, .nfinite-select-light__option--is-disabled,
    .nfinite-select__option--is-disabled *, .nfinite-select-light__option--is-disabled * {
      background-color: transparent !important; // override --is-selected rules
      color: ${Color.get('gray')} !important; // override --is-selected rules
      cursor: default !important;
    }
    
    .nfinite-select__option--is-focused, .nfinite-select-light__option--is-focused {
      background-color: ${Color.get('lightGray')} !important; // override --is-selected rules
      color: ${Color.get('lightGray', 'contrast')} !important; // override --is-selected rules
      cursor: pointer;
    }

    .nfinite-select__option--is-selected, .nfinite-select-light__option--is-selected {
      background-color: ${Color.get('primary')} !important; // override --is-selected rules
      color: ${Color.get('primary', 'contrast')} !important; // override --is-selected rules
      cursor: pointer;
    }

    .nfinite-select__menu-list--is-multi, .nfinite-select-light__menu-list--is-multi {
      .nfinite-select__option--is-focused, .nfinite-select-light__option--is-focused {
        background-color: ${Color.get('lightGray')} !important;
        color: ${Color.get('text')} !important;
      }
      .nfinite-select__option--is-selected, .nfinite-select-light__option--is-selected {
        background-color: transparent !important;
        color: ${Color.get('text')} !important;
        cursor: pointer;
        :hover {
          background-color: ${Color.get('lightGray')} !important;
          color: ${Color.get('text')} !important;
        }
      }
    }
  }
`

export const ArrowIcon = styled(IconComponent)`
  width: 8px;
  height: 4px;
  ${({ isOpen }) =>
    isOpen
      ? css`
          transform: rotate(180deg);
        `
      : css`
          transform: rotate(0deg);
        `}

  & svg {
    fill: ${Color.get('gray', 'dark')};
  }
`

export const LoadingWrapper = styled('div')`
  ${Spacing.generateMargin({ right: 'm' })}
`

export const Message = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  ${Spacing.generatePadding({ all: 's' })}
  ${Font.generate({ weight: 'bold' })}
  color: ${Color.get('text', 'gray')};
`

export const Checkbox = styled(CheckboxComponent)`
  ${Spacing.generateMargin({ vertical: '0' })};
  * {
    ${Font.generate({ size: 'default' })};
    &:hover {
      cursor: pointer;
    }
  }
`

export const GroupHeadingLabel = styled('div')`
  ${Font.generate({ size: 'xs', weight: 'bold' })}
  color: ${Color.get('text')};
  text-transform: capitalize;
`

export const TopSection = styled('div')`
  display: flex;
  justify-content: space-between;
`

export const SearchWrapper = styled('div')`
  ${Spacing.generateMargin({ horizontal: 's', vertical: 's' })};
  background-color: ${Color.get('gray', 'light')};
  ${BorderRadius.generate({ all: 'l' })};
  flex-grow: 1;
  display: flex;
  align-items: center;
`

export const SearchFilterToggle = styled(ToggleStandalone)`
  ${Spacing.generateMargin({ right: 's', vertical: 's' })};
  ${Spacing.generatePadding({ horizontal: 'xs', vertical: 'xxs' })};
  background-color: ${Color.get('gray', 'light')};
  ${BorderRadius.generate({ all: 'l' })};
`

export const OutsideArea = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`
export const Search = styled(SearchComponent.Light)`
  min-height: 20px;
  ${Font.generate({ size: 's' })}
  ${Spacing.generatePadding({ left: 'xs', vertical: 'xs' })}
  width: 100%;
`

export const Tag = styled('div')`
  display: flex;
  align-items: center;
  background-color: ${({ color }) => Color.get('tag', color)};
  ${BorderRadius.generate({ all: 'xs' })};
  padding: 2px 5px;

  &:hover {
    transform: scale3d(1.05, 1.05, 1.05);
  }
`

Tag.Icon = styled(IconComponent)`
  height: 10px;
  width: 10px;
  ${Spacing.generateMargin({ left: 'xxs', right: 'xxs' })};

  & svg {
    fill: ${Color.get('text', 'main')};
  }
`
